import React, { useState } from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { SalvButton, SalvDropdown, SalvModal, SalvTextArea } from '@salvapp/ui-components';
import { manualAlertInternalApi, universalDocumentsApi } from '../../apis';
import {
  InternalApiCreateManualAlertCommand,
  InternalApiManualAlert,
  InternalApiManualAlertRationale,
  InternalApiUniversalDocumentEntityType,
} from '../../api-internal';
import { parseErrorMessage } from '../../common/errors/helpers';
import { useDispatch } from 'react-redux';
import { showFeedback } from '../../feedback/feedbackReducer';
import { FileUploadWrapper } from '../../upload/FileUploadWrapper';
import { rationaleOptions } from './rationaleHelper';
import { useNavigate } from 'react-router';
import { getManualAlertPath } from '../../navigation/helper';

interface CreateManualAlertModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  personId: string;
  transactionId?: string;
  personName?: string;
  transactionAmount?: string;
  editingAlert?: InternalApiManualAlert | null;
}

interface CreateManualAlertFormValues {
  rationale: string;
  rationaleComment: string;
  files: File[];
}

const validationSchema = Yup.object().shape({
  rationale: Yup.string().required('Rationale is required'),
  rationaleComment: Yup.string().max(1000, 'Comment must be less than 1000 characters'),
});

export const CreateManualAlertModal = ({
  show,
  onHide,
  onSuccess,
  personId,
  transactionId,
  personName,
  editingAlert,
}: CreateManualAlertModalProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [files, setFiles] = useState<File[]>([]);

  const initialValues: CreateManualAlertFormValues = {
    rationale: editingAlert?.rationale || '',
    rationaleComment: editingAlert?.rationaleComment || '',
    files: [],
  };

  const submit = async (
    values: CreateManualAlertFormValues,
    { setSubmitting, setStatus, resetForm }: FormikHelpers<CreateManualAlertFormValues>
  ) => {
    try {
      setIsSubmitting(true);
      setSubmitting(true);

      const command: InternalApiCreateManualAlertCommand = {
        personId,
        transactionId: transactionId,
        rationale: values.rationale as InternalApiManualAlertRationale,
        rationaleComment: values.rationaleComment || undefined,
      };

      let createdAlert;
      if (editingAlert) {
        createdAlert = await manualAlertInternalApi.updateManualAlert({
          id: editingAlert.id,
          internalApiCreateManualAlertCommand: command,
        });
      } else {
        createdAlert = await manualAlertInternalApi.createManualAlert({
          internalApiCreateManualAlertCommand: command,
        });
      }

      // Upload files if any
      let fileUploadFailed = false;
      if (files.length > 0 && createdAlert) {
        try {
          for (const file of files) {
            await universalDocumentsApi.uploadDocuments({
              entityType: InternalApiUniversalDocumentEntityType.MANUAL_ALERT,
              entityId: createdAlert.id,
              file: file,
            });
          }
        } catch {
          fileUploadFailed = true;
        }
      }

      resetForm();
      setFiles([]);
      onHide();
      if (editingAlert) {
        const message = fileUploadFailed
          ? 'Manual alert updated but file upload failed. You can retry from the alert page.'
          : 'Manual alert updated successfully';
        dispatch(showFeedback({ message, success: !fileUploadFailed }));
        onSuccess();
      } else {
        const alertPath = getManualAlertPath(createdAlert.id);
        const message = fileUploadFailed
          ? `Manual alert #${createdAlert.id} created but file upload failed. Redirecting...`
          : `Manual alert #${createdAlert.id} created. Redirecting...`;
        dispatch(
          showFeedback({
            message,
            success: !fileUploadFailed,
            actionLabel: 'Stay on page',
            onAction: () => {
              onSuccess();
            },
            onDismiss: () => {
              navigate(alertPath);
            },
          })
        );
      }
    } catch (e) {
      const errorMessage = await parseErrorMessage(e);
      setStatus(errorMessage);
      dispatch(showFeedback({ message: errorMessage, success: false }));
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  const renderEntityInfo = () => {
    const displayName = personName && personName !== personId ? personName : null;

    return (
      <div className='mb-3'>
        <div className={transactionId ? 'mb-2' : ''}>
          <span className='text-secondary'>Customer: </span>
          {displayName ? (
            <span>
              {displayName} <span className='text-muted'>({personId})</span>
            </span>
          ) : (
            <span>{personId}</span>
          )}
        </div>
        {transactionId && (
          <div>
            <span className='text-secondary'>Transaction: </span>
            <span>{transactionId}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <Formik initialValues={initialValues} onSubmit={submit} validationSchema={validationSchema} enableReinitialize>
      {({ values, setFieldValue, errors, touched, resetForm }: FormikProps<CreateManualAlertFormValues>) => {
        const handleClose = () => {
          resetForm();
          setFiles([]);
          onHide();
        };

        return (
          <SalvModal show={show} onHide={handleClose}>
            <Form>
              <SalvModal.Header>{editingAlert ? 'Edit Manual Alert' : 'Create Manual Alert'}</SalvModal.Header>
              <SalvModal.Body>
                {renderEntityInfo()}

                <div className='mb-3'>
                  <SalvDropdown
                    label='Rationale'
                    value={rationaleOptions.find((option) => option.value === values.rationale)}
                    onChange={([option]) => setFieldValue('rationale', option?.value || '')}
                    options={rationaleOptions}
                    clearable={false}
                    placeholder='Select a rationale for this alert'
                  />
                  {errors.rationale && touched.rationale && (
                    <div className='salv-error-message'>{errors.rationale}</div>
                  )}
                </div>

                <div className='mb-3'>
                  <SalvTextArea
                    label='Additional Comments'
                    value={values.rationaleComment}
                    onChange={(value: string) => setFieldValue('rationaleComment', value)}
                    isInvalid={!!errors.rationaleComment && touched.rationaleComment}
                    placeholder='Optional additional details about this alert'
                  />
                  {errors.rationaleComment && touched.rationaleComment && (
                    <div className='salv-error-message'>{errors.rationaleComment}</div>
                  )}
                  <div className='form-text'>
                    This comment is saved with the alert rationale. Notes can be added separately after the alert is
                    created.
                  </div>
                </div>

                <div className='mb-3'>
                  <label className='form-label'>Attachments (Optional)</label>
                  <FileUploadWrapper files={files} onChange={setFiles} isMulti={true} maxFiles={5} />
                </div>
              </SalvModal.Body>

              <SalvModal.Footer>
                <SalvButton variant='secondary' onClick={handleClose} disabled={isSubmitting} label='Cancel' />
                <SalvButton
                  type='submit'
                  variant='primary'
                  disabled={isSubmitting}
                  label={
                    isSubmitting
                      ? editingAlert
                        ? 'Updating...'
                        : 'Creating...'
                      : editingAlert
                        ? 'Update Alert'
                        : 'Create Alert'
                  }
                />
              </SalvModal.Footer>
            </Form>
          </SalvModal>
        );
      }}
    </Formik>
  );
};

export default CreateManualAlertModal;
